import type { ConditionOperatorType } from '@pushly/pquery'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import { AI_DIRECTIVE_BINDINGS_V1, AiDirectiveBinding } from '@pushly/models/lib/enums/pushly-ai/ai-directive-binding'

/**
 * PQueryBuilder field registry for the Brand Voice "Guaranteed" (T1) spec.
 *
 * The registry is derived from the directive binding catalog (`AI_DIRECTIVE_BINDINGS_V1`
 * in `@pushly/models`) so the vocabulary the UI can render stays in lockstep with what
 * the compiler can emit — a single source of truth. Multiple bindings can target the
 * same PQuery attribute (e.g. `message.raw` for both required and banned terms), so we
 * collapse to one field per attribute and union their operators.
 *
 * Labels are curated per attribute for readability; the compiler's own binding labels
 * are phrased as authoring rules ("Disallow emojis") rather than the attribute a viewer
 * sees in a condition row ("Contains emoji").
 */
const ATTRIBUTE_LABELS: Record<string, string> = {
    'title.charLength': 'Title length (characters)',
    'body.charLength': 'Body length (characters)',
    'title.wordLength': 'Title length (words)',
    'body.wordLength': 'Body length (words)',
    'message.raw': 'Message text',
    'message.hasEmoji': 'Contains emoji',
    'message.hasExclamation': 'Contains exclamation point',
    'title.hasEmoji': 'Title contains emoji',
    'body.hasEmoji': 'Body contains emoji',
    'title.hasExclamation': 'Title contains exclamation point',
    'body.hasExclamation': 'Body contains exclamation point',
    'message.isAllCaps': 'All caps',
    'title.isAllCaps': 'Title in all caps',
    'body.isAllCaps': 'Body in all caps',
    'title.endsWithQuestion': 'Title ends with a question',
}

/**
 * Full natural-language phrasing per boolean state, keyed by attribute — feeds
 * `PQueryBuilderField.booleanDisplay` so a read-only row reads "Title does not contain
 * emoji" instead of the generic "Title contains emoji is No". Attributes not listed here
 * (there are none today) fall back to that generic phrasing.
 */
const BOOLEAN_DISPLAY_LABELS: Record<string, { true: string; false: string }> = {
    'message.hasEmoji': { true: 'Contains emoji', false: 'Does not contain emoji' },
    'message.hasExclamation': { true: 'Contains exclamation points', false: 'Does not contain exclamation points' },
    'title.hasEmoji': { true: 'Title contains emoji', false: 'Title does not contain emoji' },
    'body.hasEmoji': { true: 'Body contains emoji', false: 'Body does not contain emoji' },
    'title.hasExclamation': {
        true: 'Title contains exclamation points',
        false: 'Title does not contain exclamation points',
    },
    'body.hasExclamation': {
        true: 'Body contains exclamation points',
        false: 'Body does not contain exclamation points',
    },
    'title.endsWithQuestion': { true: 'Title ends with a question', false: 'Title does not end with a question' },
    // "All caps" alone would read as a value rather than a state, and the negative is the only
    // form these ever appear in — the binding exists to disallow shouting, never to require it.
    'message.isAllCaps': { true: 'Written in all caps', false: 'Not written in all caps' },
    'title.isAllCaps': { true: 'Title in all caps', false: 'Title not in all caps' },
    'body.isAllCaps': { true: 'Body in all caps', false: 'Body not in all caps' },
}

/** Map a binding value kind to a PQueryBuilder field type. */
function fieldTypeForKind(kind: AiDirectiveBinding['value']['kind']): PQueryBuilderField['type'] {
    switch (kind) {
        case 'integer':
            return 'integer'
        case 'string_array':
            return 'array'
        case 'flag':
            return 'boolean'
    }
}

function buildContentDirectiveFields(bindings: readonly AiDirectiveBinding[]): PQueryBuilderField[] {
    const byAttribute = new Map<string, { field: PQueryBuilderField; operators: Set<string> }>()

    for (const binding of bindings) {
        const existing = byAttribute.get(binding.attribute)
        if (existing) {
            existing.operators.add(binding.operator)
            continue
        }
        const type = fieldTypeForKind(binding.value.kind)
        byAttribute.set(binding.attribute, {
            field: {
                key: binding.attribute,
                label: ATTRIBUTE_LABELS[binding.attribute] ?? binding.label,
                type,
                ...(type === 'boolean' && BOOLEAN_DISPLAY_LABELS[binding.attribute]
                    ? { booleanDisplay: BOOLEAN_DISPLAY_LABELS[binding.attribute] }
                    : {}),
            },
            operators: new Set([binding.operator]),
        })
    }

    return Array.from(byAttribute.values()).map(({ field, operators }) => ({
        ...field,
        operators: Array.from(operators) as ConditionOperatorType[],
    }))
}

/** Field registry for rendering a compiled directive `spec` in the PQueryBuilder. */
export const CONTENT_DIRECTIVE_FIELDS: PQueryBuilderField[] = buildContentDirectiveFields(AI_DIRECTIVE_BINDINGS_V1)
